<template>
  <v-container fluid>
    <alert-unsaved-changes v-if="unsavedChanges && edit"></alert-unsaved-changes>
    <v-form ref="managementForm">
      <v-row v-if="edit">
        <v-col cols="12">
          <v-expansion-panels>
            <v-expansion-panel>
              <v-expansion-panel-header>
                {{ $t('optional-info') }}
              </v-expansion-panel-header>
              <v-expansion-panel-content class="mt-5">
                <v-row>
                  <v-col cols="12" md="6" sm="12">
                    <label>Documentation</label>
                    <p class="label-info fixed-label-height">
                      Link to the patent documentation.
                    </p>
                    <v-text-field
                      v-model.trim="optionalPatent.documentation"
                      outlined
                      :placeholder="'Paste a URL'"
                      prepend-inner-icon="mdi-link-variant"
                      clearable
                      @input="handleDocumentationUrl"
                    >
                    </v-text-field>
                    <v-alert v-if="!isValidDocumentationUrl" type="error" class="subtitle-2 py-2">
                      Please enter a valid URL
                    </v-alert>
                  </v-col>
                  <v-col cols="12" md="6" sm="12">
                    <label>Terms of License</label>
                    <p class="label-info fixed-label-height">
                      Webpage describing the rules, patent conditions and license policy which one
                      must agree to abide by in order to use the patent.
                    </p>
                    <v-text-field
                      v-model.trim="optionalPatent.termsOfLicense"
                      outlined
                      :placeholder="'Paste a URL'"
                      prepend-inner-icon="mdi-link-variant"
                      clearable
                      @input="handleTermsOfLicenseUrl"
                    >
                    </v-text-field>
                    <v-alert v-if="!isTermsOfLicenseUrl" type="error" class="subtitle-2 py-2">
                      Please enter a valid URL
                    </v-alert>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>

      <!-- Buttons -->
      <v-row class="my-5" justify="end">
        <v-btn
          v-if="unsavedChanges && edit"
          @click="cancel"
          outlined
          class="black--text font-weight-bold mx-2"
          rounded
          x-large
        >
          {{ $t('cancel') }}
        </v-btn>
        <v-btn
          v-if="unsavedChanges && edit"
          @click="updatePatentManagementInfo"
          :disabled="isUpdateDisabled"
          depressed
          rounded
          x-large
          color="primary200"
          class="white--text font-weight-bold"
        >
          {{ $t('update') }}
        </v-btn>
        <v-btn
          v-if="!unsavedChanges"
          depressed
          rounded
          x-large
          color="black"
          @click="back"
          outlined
          class="black--text font-weight-bold mx-2"
        >
          <v-icon color="black">$vuetify.icons.back</v-icon>
          {{ $t('back') }}
        </v-btn>
      </v-row>
    </v-form>
  </v-container>
</template>

<script>
import AlertUnsavedChanges from '@/components/shared/helpers/AlertUnsavedChanges';
import util from '@/mixins/util.js';
import EventBus from '@/helpers/event-bus.js';

export default {
  name: 'PatentManagementInfo',
  mixins: [util],
  components: { AlertUnsavedChanges },
  data: () => ({
    optionalPatent: {
      documentation: '',
      termsOfLicense: '',
    },
    unsavedChanges: false,
    isValidDocumentationUrl: true,
    isTermsOfLicenseUrl: true,
    initialOptionalPatent: {},
    initialStatus: {},
    dataLoaded: false,
    status: false,
  }),
  props: {
    edit: {
      type: Boolean,
      default: false,
    },
    published: {
      type: Boolean,
      default: true,
    },
  },
  watch: {
    optionalPatent: {
      deep: true,
      handler() {
        if (!this.dataLoaded) return;
        this.unsavedChanges =
          this.optionalPatent.documentation !== this.initialOptionalPatent.documentation ||
          this.optionalPatent.termsOfLicense !== this.initialOptionalPatent.termsOfLicense;
      },
    },
    published: {
      deep: true,
      handler() {
        if (!this.dataLoaded) return;
        this.unsavedChanges = this.status !== this.published;
      },
    },
  },
  mounted() {
    if (this.edit) {
      this.getPatentManagementInfo();
    }
  },
  computed: {
    isUpdateDisabled() {
      return !this.isValidDocumentationUrl || !this.isTermsOfLicenseUrl;
    },
  },

  methods: {
    back() {
      this.$emit('tab', 3);
    },
    getPatentManagementInfo() {
      EventBus.$emit('waiting', true);
      const id = this.$route.params.id;

      this.axios
        .get(`/api/v1/patent/${id}`)
        .then(response => {
          console.log('Get Patent Management Info', response.data);

          this.optionalPatent.documentation = response.data.documentation;
          this.optionalPatent.termsOfLicense = response.data.termsOfLicense;

          this.initialOptionalPatent = {
            documentation: response.data.documentation,
            termsOfLicense: response.data.termsOfLicense,
          };

          this.status = response.data.published;

          this.initialStatus = {
            published: response.data.published,
          };
          this.$emit('status', this.initialStatus.published);

          this.dataLoaded = true;
        })
        .catch(error => {
          console.error('Error fetching patent data:', error);
        })
        .finally(() => {
          EventBus.$emit('waiting', false);
        });
    },
    updatePatentManagementInfo() {
      const id = this.$route.params.id;
      const patentUpdateData = {
        published: this.published,
        id: id,
        documentation: this.optionalPatent.documentation,
        termsOfLicense: this.optionalPatent.termsOfLicense,
      };

      this.deleteEmptyProperties(patentUpdateData);

      this.axios
        .put('api/v1/patent/management-info', patentUpdateData)
        .then(response => {
          console.log('Patent info updated successfully:', response.data);
          EventBus.$emit('snackbar', {
            text: 'Patent Updated Successfully',
          });

          this.initialStatus = this.status;
          this.unsavedChanges = false;
          this.$emit('updatedChanges', true);

          this.getPatentManagementInfo();
        })
        .catch(error => {
          console.error('Error updating patent info:', error);
        });
    },
    deleteEmptyProperties(obj) {
      if (!obj || typeof obj !== 'object') {
        return;
      }

      if (Array.isArray(obj)) {
        obj.forEach((item, index) => {
          if (item === null || item === undefined || item === '') {
            obj.splice(index, 1);
          } else if (typeof item === 'object') {
            this.deleteEmptyProperties(item);
            if (Object.keys(item).length === 0) {
              obj.splice(index, 1);
            }
          }
        });
      } else {
        for (const key in obj) {
          if (obj[key] === null || obj[key] === undefined || obj[key] === '') {
            delete obj[key];
          } else if (Array.isArray(obj[key])) {
            this.deleteEmptyProperties(obj[key]);
            if (obj[key].length === 0) {
              delete obj[key];
            }
          } else if (typeof obj[key] === 'object') {
            this.deleteEmptyProperties(obj[key]);
            if (Object.keys(obj[key]).length === 0) {
              delete obj[key];
            }
          }
        }
      }
    },
    cancel() {
      this.getPatentManagementInfo();
      this.isValidDocumentationUrl = true;
      this.isTermsOfLicenseUrl = true;
    },
    handleDocumentationUrl(value) {
      if (value) {
        this.validateDocumentationUrl(value);
      } else {
        this.isValidDocumentationUrl = true;
      }
    },
    validateDocumentationUrl() {
      this.isValidDocumentationUrl = this.canParse(this.optionalPatent.documentation);
    },
    handleTermsOfLicenseUrl(value) {
      if (value) {
        this.validateTermsOfLicenseUrl(value);
      } else {
        this.isTermsOfLicenseUrl = true;
      }
    },
    validateTermsOfLicenseUrl() {
      this.isTermsOfLicenseUrl = this.canParse(this.optionalPatent.termsOfLicense);
    },
    canParse(link) {
      if (!/^https?:\/\//i.test(link)) {
        return false;
      }
      try {
        new URL(link);
        return true;
      } catch (e) {
        return false;
      }
    },
  },
};
</script>

<style scoped>
.fixed-label-height {
  min-height: 4em;
}
</style>
